import React, { useState, useRef, useCallback } from 'react';
import { Typography, Button, Alert } from 'antd';
import {
  DesktopOutlined,
  CheckCircleFilled,
  WarningFilled,
  CloseCircleFilled,
  ReloadOutlined,
  FullscreenOutlined,
  AppstoreOutlined,
  ChromeOutlined,
  SoundOutlined,
  EyeInvisibleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { BrowserInfo } from '../hooks/useBrowserDetection';
import HelpAccordions from '../components/HelpAccordions';
import '../DeviceSetupWizard.scss';

type ScreenShareState = 'initial' | 'sharing' | 'wrong-selection' | 'no-audio' | 'verified' | 'error' | 'cancelled';

interface ScreenShareStepProps {
  onComplete: () => void;
  onStreamReady: (stream: MediaStream) => void;
  isCompleted: boolean;
  browserInfo: BrowserInfo;
}

const ScreenShareStep: React.FC<ScreenShareStepProps> = ({
  onComplete,
  onStreamReady,
  isCompleted,
  browserInfo,
}) => {
  const [shareState, setShareState] = useState<ScreenShareState>('initial');
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [hasSystemAudio, setHasSystemAudio] = useState(false);
  const [hasAcknowledgedHideNotification, setHasAcknowledgedHideNotification] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const checkScreenShareType = (stream: MediaStream): 'monitor' | 'window' | 'browser' | 'unknown' => {
    const videoTrack = stream.getVideoTracks()[0];
    if (!videoTrack) return 'unknown';

    const settings = videoTrack.getSettings() as any;
    return settings.displaySurface || 'unknown';
  };

  const checkSystemAudio = (stream: MediaStream): boolean => {
    const audioTracks = stream.getAudioTracks();
    return audioTracks.length > 0 && audioTracks.some(track => track.readyState === 'live');
  };

  const startScreenShare = useCallback(async () => {
    setShareState('sharing');
    setErrorMessage('');

    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          // @ts-ignore - displaySurface is not in the type definition but works
          displaySurface: 'monitor',
          frameRate: { max: 15 },
        },
        audio: true, // Request system audio
      });

      const shareType = checkScreenShareType(stream);
      const hasAudio = checkSystemAudio(stream);

      setSelectedType(shareType);
      setHasSystemAudio(hasAudio);

      // Check if entire screen was selected
      if (shareType !== 'monitor') {
        // Wrong selection - stop the stream but stay in the modal
        stream.getTracks().forEach(track => track.stop());
        setShareState('wrong-selection');
        return;
      }

      // Correct selection (audio is optional)
      streamRef.current = stream;
      onStreamReady(stream);

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }

      // Listen for stream end
      stream.getVideoTracks()[0].addEventListener('ended', () => {
        setShareState('cancelled');
        setErrorMessage('Screen sharing was stopped.');
      });

      setShareState('verified');
      // Don't call onComplete yet - wait for user to acknowledge hide notification step
    } catch (error: any) {
      console.error('Screen share error:', error);

      if (error.name === 'NotAllowedError') {
        setShareState('cancelled');
        setErrorMessage('Screen sharing was cancelled. Click "Share Screen" to try again.');
      } else {
        setShareState('error');
        setErrorMessage('Failed to start screen sharing. Please try again.');
      }
    }
  }, [onStreamReady]);

  // Handler for when user acknowledges they've hidden the notification
  const handleHideNotificationAcknowledge = useCallback(() => {
    setHasAcknowledgedHideNotification(true);
    onComplete();
  }, [onComplete]);

  const getShareTypeLabel = (type: string): string => {
    switch (type) {
      case 'monitor':
        return 'Entire Screen';
      case 'window':
        return 'Application Window';
      case 'browser':
        return 'Browser Tab';
      default:
        return 'Unknown';
    }
  };

  const renderInitialState = () => (
    <div className="screen-share-container">
      {/* Important Notice */}
      <div className="instruction-box">
        <div className="instruction-title">
          <WarningFilled style={{ color: '#faad14' }} />
          Important: Select "Entire Screen"
        </div>
        <Typography.Text>
          You <strong>must</strong> share your entire screen (not a window or tab) for the assessment to work properly.
          This is required for proctoring purposes.
        </Typography.Text>
      </div>

      {/* Visual Guide */}
      <div className="screen-share-guide">
        <Typography.Title level={5}>
          When the dialog appears, select like this:
        </Typography.Title>

        <div className="share-options">
          <div className="share-option correct">
            <FullscreenOutlined className="option-icon" style={{ color: '#52c41a' }} />
            <Typography.Text strong>Entire Screen</Typography.Text>
            <CheckCircleFilled style={{ color: '#52c41a' }} />
          </div>
          <div className="share-option incorrect">
            <AppstoreOutlined className="option-icon" style={{ color: '#ff4d4f' }} />
            <Typography.Text>Window</Typography.Text>
            <CloseCircleFilled style={{ color: '#ff4d4f' }} />
          </div>
          <div className="share-option incorrect">
            <ChromeOutlined className="option-icon" style={{ color: '#ff4d4f' }} />
            <Typography.Text>Tab</Typography.Text>
            <CloseCircleFilled style={{ color: '#ff4d4f' }} />
          </div>
        </div>

        {browserInfo.supportsSystemAudio && (
          <Alert
            type="info"
            message={
              <span>
                <SoundOutlined /> Also check "Share system audio" for complete proctoring
              </span>
            }
            style={{ marginTop: 16 }}
          />
        )}

        <Button
          type="primary"
          size="large"
          icon={<DesktopOutlined />}
          onClick={startScreenShare}
          style={{ marginTop: 24 }}
        >
          Share Screen
        </Button>
      </div>
    </div>
  );

  const renderWrongSelection = () => (
    <div className="screen-share-container">
      <div className="error-state">
        <div className="error-header">
          <CloseCircleFilled className="error-icon" />
          <div>
            <Typography.Title level={4} style={{ margin: 0, color: '#ff4d4f' }}>
              Incorrect Selection
            </Typography.Title>
            <Typography.Text type="secondary">
              You selected "{getShareTypeLabel(selectedType)}" instead of "Entire Screen"
            </Typography.Text>
          </div>
        </div>

        {/* Visual Comparison */}
        <div className="error-comparison">
          <div className="comparison-item">
            <div className="comparison-box wrong">
              <CloseCircleFilled style={{ fontSize: 24, color: '#ff4d4f' }} />
              <Typography.Text style={{ display: 'block', marginTop: 8 }}>
                {getShareTypeLabel(selectedType)}
              </Typography.Text>
            </div>
            <Typography.Text type="secondary">What you selected</Typography.Text>
          </div>

          <Typography.Text style={{ alignSelf: 'center', fontSize: 24 }}>?</Typography.Text>

          <div className="comparison-item">
            <div className="comparison-box correct">
              <CheckCircleFilled style={{ fontSize: 24, color: '#52c41a' }} />
              <Typography.Text style={{ display: 'block', marginTop: 8 }}>
                Entire Screen
              </Typography.Text>
            </div>
            <Typography.Text type="secondary">What you need</Typography.Text>
          </div>
        </div>

        {/* Fix Steps */}
        <div className="fix-steps">
          <Typography.Title level={5}>How to fix:</Typography.Title>
          {browserInfo.browser === 'chrome' || browserInfo.browser === 'edge' ? (
            <div className="step-list">
              <div className="step-item">
                <span className="step-number">1</span>
                <span>Click "Try Again" below</span>
              </div>
              <div className="step-item">
                <span className="step-number">2</span>
                <span>In the dialog, click the <strong>"Entire Screen"</strong> tab (first tab)</span>
              </div>
              <div className="step-item">
                <span className="step-number">3</span>
                <span>Click on your screen thumbnail</span>
              </div>
              <div className="step-item">
                <span className="step-number">4</span>
                <span>Check "Share system audio" if available</span>
              </div>
              <div className="step-item">
                <span className="step-number">5</span>
                <span>Click "Share"</span>
              </div>
            </div>
          ) : (
            <div className="step-list">
              <div className="step-item">
                <span className="step-number">1</span>
                <span>Click "Try Again" below</span>
              </div>
              <div className="step-item">
                <span className="step-number">2</span>
                <span>Look for "Entire Screen" or "Full Screen" option</span>
              </div>
              <div className="step-item">
                <span className="step-number">3</span>
                <span>Select your screen/monitor</span>
              </div>
              <div className="step-item">
                <span className="step-number">4</span>
                <span>Click "Allow" or "Share"</span>
              </div>
            </div>
          )}
        </div>

        <Button
          type="primary"
          size="large"
          icon={<ReloadOutlined />}
          onClick={startScreenShare}
          style={{ marginTop: 24 }}
        >
          Try Again
        </Button>
      </div>
    </div>
  );

  const renderNoAudioState = () => (
    <div className="screen-share-container">
      <div className="error-state">
        <div className="error-header">
          <CloseCircleFilled className="error-icon" />
          <div>
            <Typography.Title level={4} style={{ margin: 0, color: '#ff4d4f' }}>
              System Audio Required
            </Typography.Title>
            <Typography.Text type="secondary">
              You need to enable "Share system audio" for proctoring
            </Typography.Text>
          </div>
        </div>

        {/* Fix Steps */}
        <div className="fix-steps">
          <Typography.Title level={5}>How to enable system audio:</Typography.Title>
          {browserInfo.browser === 'chrome' || browserInfo.browser === 'edge' ? (
            <div className="step-list">
              <div className="step-item">
                <span className="step-number">1</span>
                <span>Click "Try Again" below</span>
              </div>
              <div className="step-item">
                <span className="step-number">2</span>
                <span>Select the <strong>"Entire Screen"</strong> tab</span>
              </div>
              <div className="step-item">
                <span className="step-number">3</span>
                <span>Click on your screen thumbnail</span>
              </div>
              <div className="step-item">
                <span className="step-number">4</span>
                <span><strong>Check the "Share system audio" checkbox</strong> at the bottom</span>
              </div>
              <div className="step-item">
                <span className="step-number">5</span>
                <span>Click "Share"</span>
              </div>
            </div>
          ) : (
            <div className="step-list">
              <div className="step-item">
                <span className="step-number">1</span>
                <span>Click "Try Again" below</span>
              </div>
              <div className="step-item">
                <span className="step-number">2</span>
                <span>Select "Entire Screen"</span>
              </div>
              <div className="step-item">
                <span className="step-number">3</span>
                <span><strong>Enable audio sharing</strong> if available</span>
              </div>
              <div className="step-item">
                <span className="step-number">4</span>
                <span>Click "Allow" or "Share"</span>
              </div>
            </div>
          )}
        </div>

        <Alert
          type="info"
          message="Why is system audio required?"
          description="System audio helps us verify the integrity of your assessment environment. Without it, the proctoring system cannot function properly."
          style={{ marginTop: 16 }}
        />

        <Button
          type="primary"
          size="large"
          icon={<ReloadOutlined />}
          onClick={startScreenShare}
          style={{ marginTop: 24 }}
        >
          Try Again
        </Button>
      </div>
    </div>
  );

  const renderVerifiedState = () => {
    // Show hide notification acknowledgment step first
    if (!hasAcknowledgedHideNotification) {
      return (
        <div className="screen-share-container">
          <Alert
            type="success"
            message="Screen Sharing Active"
            description={
              <div>
                <p style={{ marginBottom: 0 }}>Your entire screen is now being shared.</p>
                {hasSystemAudio ? (
                  <p style={{ marginBottom: 0 }}><CheckCircleFilled style={{ color: '#52c41a' }} /> System audio is enabled</p>
                ) : (
                  <p style={{ marginBottom: 0 }}><WarningFilled style={{ color: '#faad14' }} /> System audio not detected (may affect proctoring)</p>
                )}
              </div>
            }
            showIcon
            icon={<CheckCircleFilled />}
            style={{ marginBottom: 16 }}
          />

          {/* Hide Notification Action Required */}
          <div style={{ 
            background: '#fff7e6', 
            border: '2px solid #ffa940',
            borderRadius: 8, 
            padding: 20,
            marginBottom: 16
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <EyeInvisibleOutlined style={{ fontSize: 24, color: '#fa8c16', marginTop: 2 }} />
              <div style={{ flex: 1 }}>
                <Typography.Title level={5} style={{ margin: 0, marginBottom: 8, color: '#d46b08' }}>
                  Action Required: Hide the Screen Sharing Bar
                </Typography.Title>
                <Typography.Text style={{ display: 'block', marginBottom: 12 }}>
                  Look at the <strong>bottom of your screen</strong>. You should see a notification bar that says 
                  "<strong>You're sharing your screen</strong>" with options.
                </Typography.Text>
                
                {/* Visual mockup of the browser bar */}
                <div style={{ 
                  background: '#262626', 
                  borderRadius: 6, 
                  padding: '10px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  color: '#fff',
                  fontSize: 13,
                  marginBottom: 12
                }}>
                  <span>🖥️ You're sharing your screen and audio</span>
                  <div style={{ display: 'flex', gap: 16 }}>
                    <span
                      style={{
                        color: '#ff7875',
                        filter: 'blur(2px)',
                        userSelect: 'none',
                        pointerEvents: 'none',
                      }}
                      aria-label="Blurred text"
                    >
                      Stop sharing
                    </span>
                    <span style={{ 
                      color: '#69c0ff', 
                      fontWeight: 600,
                      textDecoration: 'underline',
                      animation: 'pulse 1.5s infinite'
                    }}>Hide ← Click this!</span>
                  </div>
                </div>

                <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                  Clicking "Hide" will minimize the bar so it doesn't distract you during the assessment.
                </Typography.Text>

                <Button
                  type="primary"
                  size="large"
                  icon={<CheckCircleFilled />}
                  onClick={handleHideNotificationAcknowledge}
                  style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                >
                  I've Hidden it / Continue
                </Button>
              </div>
            </div>
          </div>

          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            <InfoCircleOutlined /> If you can't find the "Hide" option, click "Continue" anyway. The bar will remain visible during assessment.
          </Typography.Text>
        </div>
      );
    }

    // After acknowledgment, show normal verified state
    return (
      <div className="screen-share-container">
        <Alert
          type="success"
          message="Screen Sharing Active"
          description={
            <div>
              <p>Your entire screen is now being shared.</p>
              {hasSystemAudio ? (
                <p><CheckCircleFilled style={{ color: '#52c41a' }} /> System audio is enabled</p>
              ) : (
                <p><WarningFilled style={{ color: '#faad14' }} /> System audio not detected (may affect proctoring)</p>
              )}
            </div>
          }
          showIcon
          icon={<CheckCircleFilled />}
        />

        {/* Screen Preview */}
        <div className="screen-preview">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="preview-video"
          />
          <div className="preview-badge">
            <DesktopOutlined /> Screen sharing active
          </div>
        </div>

        <Alert
          type="warning"
          message="Keep screen sharing active"
          description="Do not click 'Stop sharing' until you complete the assessment."
          showIcon
        />
      </div>
    );
  };

  const renderCancelledState = () => (
    <div className="screen-share-container">
      <Alert
        type="warning"
        message="Screen Sharing Required"
        description={errorMessage || "Screen sharing is required to continue with the assessment."}
        showIcon
        action={
          <Button icon={<ReloadOutlined />} onClick={startScreenShare}>
            Share Screen
          </Button>
        }
      />

      {renderInitialState()}
    </div>
  );

  const renderContent = () => {
    switch (shareState) {
      case 'initial':
        return renderInitialState();
      case 'sharing':
        return (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <Typography.Title level={4}>
              Select your screen in the browser dialog...
            </Typography.Title>
            <Typography.Text type="secondary">
              Remember to select "Entire Screen" and check "Share system audio"
            </Typography.Text>
          </div>
        );
      case 'wrong-selection':
        return renderWrongSelection();
      case 'no-audio':
        return renderNoAudioState();
      case 'verified':
        return renderVerifiedState();
      case 'cancelled':
      case 'error':
        return renderCancelledState();
      default:
        return renderInitialState();
    }
  };

  return (
    <div className="setup-step screen-share-step">
      {/* Header */}
      <div className="step-header">
        <Typography.Title level={5} className="step-title">
          <DesktopOutlined /> Share Your Screen
        </Typography.Title>
        <Typography.Text className="step-description" type="secondary">
          Share your entire screen for proctoring during the assessment
        </Typography.Text>
      </div>

      {/* Main Content */}
      <div className="step-main">
        <div className="step-content">
          {renderContent()}
        </div>

        {/* Help Section */}
        <div className="step-help">
          <HelpAccordions type="screen" browserInfo={browserInfo} />
        </div>
      </div>
    </div>
  );
};

export default ScreenShareStep;
